import { Link } from '@tanstack/react-router'
import { ImageOff, Mail, Package, Send } from 'lucide-react'
import { ChoicePicker, type PickerChoice } from '@/components/choice-picker'
import { LoadingButton } from '@/components/loading-button'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn, parseUTCDate } from '@/lib/utils'
import {
  WARRANTY_CLAIM_STATUS,
  useSendWarrantyClaim,
  useUpdateWarrantyClaim,
  useWarrantyClaimPhoto,
  type WarrantyClaim,
  type WarrantyClaimStatus,
  type WarrantyClaimUpdate,
} from './api'
import { WarrantyClaimActions } from './claim-actions'

const CHOICES: PickerChoice[] = (Object.keys(WARRANTY_CLAIM_STATUS) as WarrantyClaimStatus[]).map((value) => ({
  value,
  label: WARRANTY_CLAIM_STATUS[value].label,
  tone: WARRANTY_CLAIM_STATUS[value].tone,
}))

/**
 * Warranty claims customers filed from their order tracker (USA-915), on a purchase order's or a
 * customer's page: what they told us and their photos, the email to the manufacturer, the status and
 * note the customer sees, and the claim's action items on the PO action cards. ``onPurchaseOrder``
 * leaves out the link to the page it is on; ``titled`` is false where a tab already names it;
 * ``focusActionUuid`` is the item a link opened.
 */
export function WarrantyClaimsSection({
  claims,
  onPurchaseOrder = false,
  titled = true,
  focusActionUuid,
}: {
  claims: WarrantyClaim[] | undefined
  onPurchaseOrder?: boolean
  titled?: boolean
  focusActionUuid?: string
}) {
  if (!claims?.length) return null
  const items = claims.map((claim) => (
    <ClaimItem key={claim.uuid} claim={claim} onPurchaseOrder={onPurchaseOrder} focusActionUuid={focusActionUuid} />
  ))
  // On a purchase order the claims sit under Shipping, headed and carded like its other sections.
  if (onPurchaseOrder) {
    return (
      <section id="warranty-claims" className="scroll-mt-4 space-y-4" aria-labelledby="warranty-claims-heading">
        <h3
          id="warranty-claims-heading"
          className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
        >
          {claims.length > 1 ? `Warranty claims (${claims.length})` : 'Warranty claim'}
        </h3>
        <Card className="gap-0 py-0">
          <ul className="divide-y">{items}</ul>
        </Card>
      </section>
    )
  }
  return (
    <section
      id="warranty-claims"
      className="scroll-mt-4 space-y-3"
      aria-label={titled ? undefined : 'Warranty claims'}
      aria-labelledby={titled ? 'warranty-claims-heading' : undefined}
    >
      {titled && (
        <h3 id="warranty-claims-heading" className="text-base font-semibold">
          Warranty claims
        </h3>
      )}
      <ul className="divide-y rounded-lg border bg-card">{items}</ul>
    </section>
  )
}

function ClaimItem({
  claim,
  onPurchaseOrder,
  focusActionUuid,
}: {
  claim: WarrantyClaim
  onPurchaseOrder: boolean
  focusActionUuid?: string
}) {
  const update = useUpdateWarrantyClaim()
  const send = useSendWarrantyClaim()
  const status = WARRANTY_CLAIM_STATUS[claim.status]
  const save = (change: WarrantyClaimUpdate) => update.mutate({ uuid: claim.uuid, update: change })

  const indent = 'sm:pl-[3.25rem]'
  return (
    <li className="space-y-3 p-4">
      <div className="flex items-start gap-3">
        <ItemThumb url={claim.image_url} />
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <p className="min-w-0 text-sm font-medium">{claim.item_name}</p>
            <ChoicePicker
              title="Claim status"
              label={`Claim status: ${status.label}. Change status`}
              value={claim.status}
              choices={CHOICES}
              onSelect={(value) => save({ status: value as WarrantyClaimStatus })}
              pending={update.isPending && update.variables?.update.status !== undefined}
            >
              <StatusPill tone={status.tone}>{status.label}</StatusPill>
            </ChoicePicker>
            {claim.unit_down && <StatusPill tone="danger">Not working</StatusPill>}
          </div>
          <p className="text-xs text-muted-foreground">
            {[
              claim.number,
              `Filed ${parseUTCDate(claim.submitted_at).toLocaleDateString()}`,
              onPurchaseOrder ? null : claim.manufacturer_name,
            ]
              .filter(Boolean)
              .join(' · ')}
            {!onPurchaseOrder && claim.po_uuid && claim.po_number && (
              <>
                {' · '}
                <Link
                  to="/purchase-orders/$poId"
                  params={{ poId: claim.po_uuid }}
                  className="text-primary hover:underline"
                >
                  {claim.po_number}
                </Link>
              </>
            )}
            {' · '}
            <Link
              to="/projects/$projectId"
              params={{ projectId: claim.project_uuid }}
              className="text-primary hover:underline"
            >
              {claim.project_name}
            </Link>
          </p>
        </div>
      </div>

      <p className={cn('whitespace-pre-line text-sm', indent)}>{claim.problem}</p>

      {claim.photos.length > 0 && (
        <ul className={cn('flex flex-wrap gap-2', indent)} aria-label="Customer's photos">
          {claim.photos.map((photo, index) => (
            <Photo key={photo.uuid} claimUuid={claim.uuid} photoUuid={photo.uuid} index={index + 1} />
          ))}
        </ul>
      )}

      <dl
        className={cn(
          'grid gap-x-4 gap-y-2 text-sm',
          'sm:grid-cols-[8.5rem_minmax(0,1fr)] sm:gap-x-6',
          indent,
        )}
      >
        <dt className="text-muted-foreground">Serial</dt>
        <dd className="font-mono text-xs leading-5">{claim.serial_number ?? 'Not given'}</dd>
        <dt className="text-muted-foreground">Contact</dt>
        <dd className="min-w-0 break-words">
          {claim.contact_name} ·{' '}
          {/* eslint-disable-next-line no-restricted-syntax -- phone link */}
          <a href={`tel:${claim.contact_phone}`} className="text-primary hover:underline">
            {claim.contact_phone}
          </a>{' '}
          ·{' '}
          {claim.customer_thread_id != null ? (
            <Link
              to="/customer-comms/$threadId"
              params={{ threadId: String(claim.customer_thread_id) }}
              className="text-primary hover:underline"
            >
              {claim.contact_email}
            </Link>
          ) : (
            claim.contact_email
          )}
        </dd>
        <dt className="self-center text-muted-foreground">Manufacturer claim</dt>
        <dd className="min-w-0">
          <POInlineEditableField
            value={claim.manufacturer_claim_number ?? ''}
            placeholder="Add their claim number"
            ariaLabel="Manufacturer claim number"
            buttonClassName={cn('-ml-2', !claim.manufacturer_claim_number && 'text-muted-foreground')}
            onConfirm={(value) => save({ manufacturer_claim_number: value.trim() || null })}
          />
        </dd>
        <dt className="self-start pt-1 text-muted-foreground">Note to customer</dt>
        <dd className="min-w-0">
          <POInlineEditableField
            kind="textarea"
            value={claim.customer_note ?? ''}
            placeholder="Add a note for the customer"
            ariaLabel="Note to customer"
            buttonClassName={cn('-ml-2', !claim.customer_note && 'text-muted-foreground')}
            allowWrap
            onConfirm={(value) => save({ customer_note: value.trim() || null })}
          />
        </dd>
      </dl>

      {/* Sent on its own when filed; only a claim that couldn't go out offers sending it. */}
      {claim.po_uuid && (
        <div className={indent}>
          {claim.manufacturer_thread_uuid ? (
            <Button asChild size="sm" variant="outline">
              <Link
                to="/purchase-orders/$poId"
                params={{ poId: claim.po_uuid }}
                search={{ thread: claim.manufacturer_thread_uuid }}
              >
                <Mail aria-hidden="true" />
                Manufacturer emails
              </Link>
            </Button>
          ) : (
            <LoadingButton
              size="sm"
              variant="outline"
              loading={send.isPending}
              loadingText="Sending…"
              onClick={() => send.mutate(claim.uuid)}
            >
              <Send aria-hidden="true" />
              Send to manufacturer
            </LoadingButton>
          )}
        </div>
      )}

      {/* What only a person can do about the claim, on the PO action cards; closed ones fold away. */}
      <WarrantyClaimActions claim={claim} focusActionUuid={focusActionUuid} className={indent} />
    </li>
  )
}

function ItemThumb({ url }: { url?: string | null }) {
  if (!url) {
    return (
      <span className="grid size-10 shrink-0 place-items-center rounded-md bg-muted">
        <Package aria-hidden="true" className="size-4 text-muted-foreground" />
      </span>
    )
  }
  return <img alt="" src={url} loading="lazy" className="size-10 shrink-0 rounded-md border bg-white object-contain p-0.5" />
}

function Photo({ claimUuid, photoUuid, index }: { claimUuid: string; photoUuid: string; index: number }) {
  const photo = useWarrantyClaimPhoto(claimUuid, photoUuid)
  const box = 'grid size-16 place-items-center overflow-hidden rounded-md border bg-muted'
  if (photo.error) {
    return (
      <li className={box} title={photo.error instanceof Error ? photo.error.message : undefined}>
        <ImageOff aria-hidden="true" className="size-4 text-muted-foreground" />
        <span className="sr-only">Photo {index} did not load</span>
      </li>
    )
  }
  return (
    <li className={box}>
      {photo.url ? (
        // eslint-disable-next-line no-restricted-syntax -- the customer's photo, already fetched with the staff token
        <a href={photo.url} target="_blank" rel="noopener noreferrer" className="block size-full">
          <img alt={`Customer photo ${index}`} src={photo.url} className="size-full object-cover" />
        </a>
      ) : (
        <span className="sr-only">Loading photo {index}</span>
      )}
    </li>
  )
}
